/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {  useNavigate } from 'react-router-dom';

import moment from 'moment';

/* Importing the components from the material-ui library. */
import { Breadcrumbs, Container, Grid, Stack, Typography } from '@mui/material';

/* Importing the functions from the Redux slices. */
import { getQuestionnaireAll } from '../../features/questionnaire/questionnaireSlice';

/* Importing the Page and MaterialTable components from the components folder. */
import MaterialTable from '../../components/MaterialTable';
import Page from '../../components/Page';

/**
 * Renders the QuestionnaireList component.
 * This component displays a list of questionnaires in a table format.
 * It fetches the questionnaires from the redux store and formats them for display.
 * The table allows users to click on a row to navigate to a questionnaire response page.
 *
 * @returns {JSX.Element} The rendered QuestionnaireList component.
 */
export default function QuestionnaireList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* Getting the state from the redux store. */
  const Questionnaires = useSelector((state) => state.questionnaire.Questionnaires);

  /* Setting the initial state of the component. */
  const [title] = useState('Questionnaire List');
  const [isLoading, setIsLoading] = useState(true);
  const [QuestionnairesFormatted, setQuestionnaireFormatted] = useState([]);

  /**
   * Format the questionnaires in the questionnaireState.Questionnaires array.
   */
  const formatQuestionnaires = useCallback(() => {
    const questionnairesArray = Questionnaires.map((questionnaire) => ({
      id: questionnaire.id,
      title: questionnaire.title,
    }));
    setQuestionnaireFormatted(questionnairesArray);
  }, [Questionnaires]);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(async () => {
    setIsLoading(true);
    await dispatch(getQuestionnaireAll());
    setIsLoading(false);
  }, [dispatch]);

  /* Calling the initializeData function. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Calling the formatQuestionnaires function. */
  useEffect(() => {
    formatQuestionnaires();
  }, [formatQuestionnaires]);

  /**
   * The columns for the table that displays the questionnaires.
   */
  const columns = [
    {
      title: 'ID',
      field: 'id',
      hidden: true,
    },
    {
      title: 'Name',
      field: 'title',
    },
  ];

  /**
   * Renders the table
   */
  const renderTable = () => (
    <MaterialTable
      onRowClick={(event, rowData) => navigate(`/questionnaireresponse/list/${rowData.id}`, { replace: false })}
      options={{
        search: true,
        exportButton: true,
        exportFileName: moment().format('YYYY-MM-DD HH:mm:ss'),
      }}
      title={''}
      isLoading={isLoading}
      columns={columns}
      data={QuestionnairesFormatted}
    />
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              <Typography color="text.primary">Questionnaire</Typography>
            </Breadcrumbs>
          </Stack>
        </Stack>

        <Grid container>
          <Grid item xs={12}>
            {renderTable()}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
